/**
 * Keep the maneuver baseline store's derived state in step with the page.
 *
 * The stored fit arrives whole from `GET /api/baselines/:id` -- every config, every maneuver type,
 * every channel -- so the series for what is currently on screen is a synchronous derivation of
 * (fit, config, event type). No fetching here.
 *
 * The TWS selector decides which surfaces are live:
 *   ALL          -> the scatter draws the whole curve, clipped to the runs' wind range. There is no
 *                   single reference maneuver to average tracks from, so the map/timeseries overlays stay off.
 *   a wind bin   -> the scatter still draws that whole curve, clipped to the same x range. The bin is
 *                   the reference the tables and the pinned row compare against, and the wind speed the
 *                   member maneuvers' tracks are averaged at for the map and timeseries overlays.
 *
 * Two stages, because they cost different things: the series is a synchronous derivation of the fit
 * already in hand, while the tracks are fetched per wind speed (see `maneuverBaselineTracks.ts`).
 */

import { createEffect, onCleanup, type Accessor } from 'solid-js';
import { maneuverBaselineStore } from '../store/maneuverBaselineStore';
import { maneuverFitToSeries, maneuverBaselineMembers } from './maneuverBaselineFit';
import {
  loadManeuverBaselineTracks,
  clearManeuverBaselineTrackCache,
  emptyManeuverBaselineTracks,
} from './maneuverBaselineTracks';
import { setTriggerUpdate } from '../store/selectionStore';
import { persistantStore } from '../store/persistantStore';
import { phase, normalized } from '../store/globalStore';
import { error as logError } from './console';
import type { ManeuversContext } from './maneuversConfig';

export interface UseManeuverBaselineLoaderOptions {
  /** The page's TWS selection: a bin centre in kph, or 'ALL'. */
  tws: Accessor<string>;
  /** The page's maneuver type — the fit is bucketed by it. */
  eventType: Accessor<string>;
  /**
   * Which maneuvers API the page reads. Needed for the map/timeseries track fetches; omit it and the
   * page gets the scatter and table overlays only.
   */
  context?: ManeuversContext;
}

/** The selected TWS bin centre in kph, or null when the page is showing every wind speed. */
export function twsBinValue(tws: string | null | undefined): number | null {
  const n = Number(tws);
  return Number.isFinite(n) ? n : null;
}

/**
 * Derive `baselineSeries` / `baselineActive` / `baselineScatterAllActive` from the selection.
 *
 * Call once per maneuver page. Clears the store on unmount: the store is shared across the maneuver
 * pages, so leaving one must not leave its overlay showing on the next.
 */
export function useManeuverBaselineLoader(options: UseManeuverBaselineLoaderOptions) {
  createEffect(() => {
    const fit = maneuverBaselineStore.baselineFit();
    const config = maneuverBaselineStore.baselineConfig();
    const type = options.eventType();
    // Tracked so a TWS change re-runs this and flips the two active flags.
    const bin = twsBinValue(options.tws());
    // Tracked so re-selecting a different baseline with the same name repaints.
    maneuverBaselineStore.overlayRevision();

    const series = fit && config ? maneuverFitToSeries(fit, config, type) : [];
    const hasSeries = series.length > 0;

    maneuverBaselineStore.setBaselineSeries(hasSeries ? series : null);
    maneuverBaselineStore.setBaselineActive(hasSeries && bin != null);
    maneuverBaselineStore.setBaselineScatterAllActive(hasSeries && bin == null);

    // The maneuver charts rebuild off triggerUpdate; without this the curve appears only on the
    // next unrelated redraw.
    setTriggerUpdate(true);
  });

  /**
   * Stage two: the averaged member tracks for the map and the timeseries.
   *
   * Separate from the effect above because it is asynchronous — the fit is already in hand, but the
   * member maneuvers' tracks are not. Only runs for a specific wind speed: TWS = ALL has no single
   * reference maneuver to average, so the scatter's fitted curve is the whole overlay there.
   */
  let controller: AbortController | null = null;
  let lastGeometryKey = '';

  createEffect(() => {
    const fit = maneuverBaselineStore.baselineFit();
    const config = maneuverBaselineStore.baselineConfig();
    const baselineId = maneuverBaselineStore.baselineId();
    const type = options.eventType();
    const bin = twsBinValue(options.tws());
    const currentPhase = phase();
    const isNormalized = normalized();
    maneuverBaselineStore.overlayRevision();

    // The cached averages are per (baseline, config, tws, phase, mirror) already, but the phase and
    // mirror switches also change which geometry the page is asking for, so drop the lot rather than
    // letting a session accumulate one entry per combination the user passed through.
    const geometryKey = `${baselineId ?? ''}|${config}|${currentPhase}|${isNormalized}`;
    if (geometryKey !== lastGeometryKey) {
      lastGeometryKey = geometryKey;
      clearManeuverBaselineTrackCache();
    }

    const abort = () => {
      controller?.abort();
      controller = null;
    };

    // Every bail-out clears `baselineLoading`: the views hide the overlay while it is true.
    const bail = () => {
      abort();
      maneuverBaselineStore.setBaselineTracks(null);
      maneuverBaselineStore.setBaselineLoading(false);
    };

    if (!options.context || !fit || !config || baselineId == null || bin == null) { bail(); return; }

    const className = persistantStore.selectedClassName();
    const projectId = Number(persistantStore.selectedProjectId());
    if (!className || !Number.isFinite(projectId) || projectId <= 0) { bail(); return; }

    const members = maneuverBaselineMembers(fit, config, type);
    if (members.length === 0) { bail(); return; }

    abort();
    controller = new AbortController();
    const signal = controller.signal;
    maneuverBaselineStore.setBaselineLoading(true);

    void loadManeuverBaselineTracks({
      context: options.context,
      className,
      projectId,
      baselineId,
      config,
      eventType: type,
      members,
      tws: bin,
      phase: currentPhase,
      isNormalized,
      signal,
    })
      .then((tracks) => {
        if (signal.aborted) { return; }
        maneuverBaselineStore.setBaselineTracks(tracks);
        maneuverBaselineStore.setBaselineLoading(false);
        setTriggerUpdate(true);
      })
      .catch((err: unknown) => {
        if (signal.aborted) { return; }
        logError('[useManeuverBaselineLoader] track load failed', err);
        maneuverBaselineStore.setBaselineTracks(emptyManeuverBaselineTracks());
        maneuverBaselineStore.setBaselineLoading(false);
      });
  });

  onCleanup(() => {
    controller?.abort();
    clearManeuverBaselineTrackCache();
    maneuverBaselineStore.clear();
  });
}
